import {
    accountLoader,
    createAccount,
    createManyAccounts,
    fetchAdminAccounts,
    fetchOwnerAccounts,
    getAccountPermissions,
    getAccounts,
    getAccountsForSubject,
    getExecutorList,
    getFirmMemberAccounts,
    updateAccountPermission,
} from './http/accounts';
import { getAccountExecutorVersion } from './http/trading-ws/accounts';
import type { DefaultError, QueryObserverOptions, QueryClient } from '@tanstack/query-core';
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import type { ConcreteAccount, NewAccount } from '@thinkalpha/trading-ws-client/contracts/account.js';
import type { UpdateAccountPermission } from 'src/lib/accounts/types';

// Constants
const ACCOUNTS = 'accounts';
const ACCOUNT_PERMISSION = 'account-permission';
const EXECUTOR_LIST = 'executor-list';
const EXECUTOR_VERSION = 'executor-version';

/**
 * Fetch an account by ID.
 *
 * Returns undefined if no account id is provided.
 *
 */
export const getAccountByIdQuery = (id?: string): QueryObserverOptions<ConcreteAccount | undefined> => ({
    queryKey: [ACCOUNTS, id],
    queryFn: () => accountLoader.load(id!),
    enabled: !!id,
});

export const getAccountsQuery = () => ({
    queryKey: [ACCOUNTS],
    queryFn: () => getAccounts(),
});

export const getAccountsByPermissionQuery = (permission: PermissionType) => {
    const queryKey = [ACCOUNTS];

    // for reader permission we use the default getAccountsQuery key of [ACCOUNTS]
    // otherwise we append the permission to the query key
    if (permission !== 'reader') {
        queryKey.push(permission);
    }

    return {
        queryKey,
        queryFn: () => getAccounts(permission),
    };
};

/**
 * @deprecated Use getAccountsByPermissionQuery instead
 */
export const getAdminAccountsListQuery = () => ({
    queryKey: [ACCOUNTS, 'admin'],
    queryFn: fetchAdminAccounts,
});

/**
 * @deprecated Use getAccountsByPermissionQuery instead
 */
export const getOwnerAccountsListQuery = () => ({
    queryKey: [ACCOUNTS, 'owner'],
    queryFn: fetchOwnerAccounts,
});

export const getAccountPermissionsQuery = (id?: string) => ({
    queryKey: [ACCOUNTS, 'permissions', id],
    queryFn: () => getAccountPermissions(id!),
    enabled: !!id,
});

export const getAccountsForSubjectQuery = (subjectId: string | undefined, forWhomType: 'user' | 'firm' | 'group') => ({
    queryKey: [ACCOUNTS, 'subject', forWhomType, subjectId],
    queryFn: () => getAccountsForSubject({ subjectId: subjectId!, forWhomType }),
    enabled: !!subjectId,
});

export const getFirmMemberAccountsQuery = (firmId: string | undefined, memberId: string | undefined) => ({
    queryKey: [ACCOUNTS, 'firm-member', firmId, memberId],
    queryFn: () => getFirmMemberAccounts(firmId!, memberId!),
    enabled: !!firmId && !!memberId,
});

export const getExecutorListQuery = () => {
    return {
        queryKey: [EXECUTOR_LIST],
        queryFn: () => getExecutorList(),
    };
};

export const getAccountExecutorVersionQuery = (
    accountId: string | undefined,
): UseQueryOptions<
    Awaited<ReturnType<typeof getAccountExecutorVersion>>,
    DefaultError,
    Awaited<ReturnType<typeof getAccountExecutorVersion>>
> => {
    return {
        queryKey: [ACCOUNTS, accountId, EXECUTOR_VERSION],
        queryFn: () => getAccountExecutorVersion(accountId!),
        enabled: Boolean(accountId),
    };
};

const VALID_MINIMUM_EXECUTOR_VERSION = 2;
const selectIsAccountConfiguredForAdminRisk = (data: number) => {
    return { isAccountConfiguredForAdminRisk: data >= VALID_MINIMUM_EXECUTOR_VERSION };
};

export const getIsAccountConfiguredForAdminRiskQuery = (
    accountId: string | undefined,
): UseQueryOptions<
    Awaited<ReturnType<typeof getAccountExecutorVersion>>,
    DefaultError,
    { isAccountConfiguredForAdminRisk: boolean }
> => {
    return {
        ...getAccountExecutorVersionQuery(accountId),
        select: selectIsAccountConfiguredForAdminRisk,
    };
};

export const createAccountMutation = (
    mutationOptions: UseMutationOptions<ConcreteAccount, Error, NewAccount>,
): UseMutationOptions<ConcreteAccount, Error, NewAccount> => {
    return {
        mutationFn: createAccount,
        ...mutationOptions,
    };
};

export const createManyAccountsMutation = (
    mutationOptions: UseMutationOptions<ConcreteAccount[], Error, NewAccount[]>,
): UseMutationOptions<ConcreteAccount[], Error, NewAccount[]> => {
    return {
        mutationFn: createManyAccounts,
        ...mutationOptions,
    };
};

export const updateAccountPermissionMutation = (
    mutationOptions: UseMutationOptions<ConcreteAccount, Error, UpdateAccountPermission>,
): UseMutationOptions<ConcreteAccount, Error, UpdateAccountPermission> => {
    return {
        mutationFn: updateAccountPermission,
        ...mutationOptions,
    };
};

export const invalidateAccountsQuery = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: [ACCOUNTS] });
};

export const invalidateAccountPermissionQuery = (queryClient: QueryClient, accountId: string) => {
    queryClient.invalidateQueries({ queryKey: [ACCOUNTS, ACCOUNT_PERMISSION, accountId] });
};
